import React, { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Box,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
} from '@mui/material';
import {
  Emergency as EmergencyIcon,
  Phone as PhoneIcon,
  Warning as WarningIcon,
  AccessTime as TimeIcon,
  LocalHospital as HospitalIcon,
  Security as SecurityIcon,
} from '@mui/icons-material';

interface CrisisKeyword {
  word: string;
  severity: 'high' | 'medium' | 'low';
  category: 'medical' | 'legal' | 'violence' | 'time-sensitive';
}

interface EmergencyContact {
  name: string;
  number: string;
  description: string;
  icon: React.ReactNode;
  when: string;
}

interface CrisisDetectorProps {
  text?: string;
  onCrisisDetected?: (severity: 'high' | 'medium' | 'low', keywords: string[]) => void;
  showAlert?: boolean;
}

const CrisisDetector: React.FC<CrisisDetectorProps> = ({
  text = "",
  onCrisisDetected,
  showAlert = true,
}) => {
  const [detectedCrisis, setDetectedCrisis] = useState<{
    severity: 'high' | 'medium' | 'low';
    keywords: string[];
    category: string;
  } | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  // Crisis keywords database
  const crisisKeywords: CrisisKeyword[] = [
    // High severity - immediate emergency
    { word: 'sebevražda', severity: 'high', category: 'medical' },
    { word: 'zabít', severity: 'high', category: 'violence' },
    { word: 'smrt', severity: 'high', category: 'medical' },
    { word: 'násilí', severity: 'high', category: 'violence' },
    { word: 'týrání', severity: 'high', category: 'violence' },
    { word: 'úraz', severity: 'high', category: 'medical' },
    { word: 'nehoda', severity: 'high', category: 'medical' },
    { word: 'krvácení', severity: 'high', category: 'medical' },
    { word: 'ztráta vědomí', severity: 'high', category: 'medical' },
    { word: 'infarkt', severity: 'high', category: 'medical' },
    { word: 'mrtvice', severity: 'high', category: 'medical' },
    
    // Medium severity - urgent legal matters
    { word: 'okamžitě', severity: 'medium', category: 'time-sensitive' },
    { word: 'ihned', severity: 'medium', category: 'time-sensitive' },
    { word: 'dnes', severity: 'medium', category: 'time-sensitive' },
    { word: 'zítra', severity: 'medium', category: 'time-sensitive' },
    { word: 'lhůta', severity: 'medium', category: 'time-sensitive' },
    { word: 'termín', severity: 'medium', category: 'time-sensitive' },
    { word: 'deadline', severity: 'medium', category: 'time-sensitive' },
    { word: 'soud', severity: 'medium', category: 'legal' },
    { word: 'zatčení', severity: 'medium', category: 'legal' },
    { word: 'policie', severity: 'medium', category: 'legal' },
    { word: 'výslech', severity: 'medium', category: 'legal' },
    { word: 'obvinění', severity: 'medium', category: 'legal' },
    { word: 'trestní', severity: 'medium', category: 'legal' },
    
    // Low severity - standard urgent matters
    { word: 'urgentní', severity: 'low', category: 'time-sensitive' },
    { word: 'naléhavé', severity: 'low', category: 'time-sensitive' },
    { word: 'rychle', severity: 'low', category: 'time-sensitive' },
    { word: 'pomoc', severity: 'low', category: 'time-sensitive' },
    { word: 'problém', severity: 'low', category: 'time-sensitive' },
  ];

  const emergencyContacts: EmergencyContact[] = [
    {
      name: 'Integrovaný záchranný systém',
      number: '112',
      description: 'Univerzální číslo pro všechny nouze',
      icon: <EmergencyIcon sx={{ color: '#f44336' }} />,
      when: 'Jakákoliv životní nouze'
    },
    {
      name: 'Záchranná služba',
      number: '155',
      description: 'Lékařská záchranná služba',
      icon: <HospitalIcon sx={{ color: '#f44336' }} />,
      when: 'Lékařská pomoc, úrazy'
    },
    {
      name: 'Policie ČR',
      number: '158',
      description: 'Policie České republiky',
      icon: <SecurityIcon sx={{ color: '#1F1F1F' }} />,
      when: 'Trestní činy, násilí, bezpečnost'
    },
    {
      name: 'Hasiči',
      number: '150',
      description: 'Hasičský záchranný sbor',
      icon: <WarningIcon sx={{ color: '#ff9800' }} />,
      when: 'Požáry, technické záchranné práce'
    },
    {
      name: 'Krízová linka',
      number: '116 123',
      description: 'Linka bezpečí - psychologická pomoc',
      icon: <PhoneIcon sx={{ color: '#4caf50' }} />,
      when: 'Psychická krize, sebevražedné myšlenky'
    },
    {
      name: 'Bezplatná právní pomoc',
      number: '800 888 388',
      description: 'Státní bezplatná právní pomoc',
      icon: <PhoneIcon sx={{ color: '#9c27b0' }} />,
      when: 'Urgentní právní záležitosti'
    },
  ];

  useEffect(() => {
    if (!text) return;

    const textLower = text.toLowerCase();
    const detectedKeywords: { word: string; severity: 'high' | 'medium' | 'low'; category: string }[] = [];

    // Check for crisis keywords
    crisisKeywords.forEach(keyword => {
      if (textLower.includes(keyword.word.toLowerCase())) {
        detectedKeywords.push({
          word: keyword.word,
          severity: keyword.severity,
          category: keyword.category
        });
      }
    });

    if (detectedKeywords.length > 0) {
      // Determine highest severity
      const highSeverity = detectedKeywords.find(k => k.severity === 'high');
      const mediumSeverity = detectedKeywords.find(k => k.severity === 'medium');
      
      const severity = highSeverity ? 'high' : mediumSeverity ? 'medium' : 'low';
      const category = detectedKeywords[0].category;

      const crisis = {
        severity: severity as 'high' | 'medium' | 'low',
        keywords: detectedKeywords.map(k => k.word),
        category
      };

      setDetectedCrisis(crisis);
      
      if (onCrisisDetected) {
        onCrisisDetected(severity, crisis.keywords);
      }

      // Auto-open dialog for high severity
      if (severity === 'high') {
        setDialogOpen(true);
      }
    } else {
      setDetectedCrisis(null);
    }
  }, [text, onCrisisDetected]);

  const handleEmergencyCall = (number: string) => {
    if (window.confirm(`Chcete zavolat ${number}?`)) {
      window.open(`tel:${number}`);
    }
  };

  const getSeverityColor = (severity: 'high' | 'medium' | 'low') => {
    switch (severity) {
      case 'high': return 'error';
      case 'medium': return 'warning';
      case 'low': return 'info';
    }
  };

  const getSeverityText = (severity: 'high' | 'medium' | 'low') => {
    switch (severity) {
      case 'high': return 'KRITICKÁ SITUACE';
      case 'medium': return 'Urgentní záležitost';
      case 'low': return 'Časově citlivé';
    }
  };

  const getCategoryAdvice = (category: string) => {
    switch (category) {
      case 'medical':
        return 'Pokud někdo potřebuje lékařskou pomoc, okamžitě volejte záchrannou službu!';
      case 'violence':
        return 'V případě násilí nebo ohrožení okamžitě kontaktujte policii!';
      case 'legal':
        return 'Důležité právní záležitosti vyžadují okamžitou pozornost kvalifikovaného advokáta.';
      case 'time-sensitive':
        return 'Tato situace vyžaduje rychlé jednání. Neváhejte kontaktovat odborníky.';
      default:
        return 'Pokud potřebujete okamžitou pomoc, neváhejte kontaktovat příslušné služby.';
    }
  };

  if (!detectedCrisis || !showAlert) return null;

  return (
    <>
      {/* Crisis Alert */}
      <Alert
        severity={getSeverityColor(detectedCrisis.severity)}
        sx={{
          mb: 2,
          borderRadius: 3,
          '& .MuiAlert-message': {
            width: '100%'
          }
        }}
        action={
          <Button
            size="small"
            onClick={() => setDialogOpen(true)}
            startIcon={<EmergencyIcon />}
            sx={{
              color: '#1F1F1F',
              '&:hover': { backgroundColor: 'rgba(31, 31, 31, 0.08)' }
            }}
          >
            Nouzové kontakty
          </Button>
        }
      >
        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
          {getSeverityText(detectedCrisis.severity)}
        </Typography>
        <Typography variant="body2">
          {getCategoryAdvice(detectedCrisis.category)}
        </Typography>
        <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
          {detectedCrisis.keywords.map((keyword, index) => (
            <Chip
              key={index}
              label={keyword}
              size="small"
              color={getSeverityColor(detectedCrisis.severity)}
              variant="outlined"
            />
          ))}
        </Box>
      </Alert>

      {/* Emergency Contacts Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            maxHeight: '90vh'
          }
        }}
      >
        <DialogTitle sx={{ 
          bgcolor: detectedCrisis.severity === 'high' ? '#ffebee' : '#fff3e0',
          borderBottom: '1px solid #e0e0e0'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <EmergencyIcon sx={{ mr: 1, color: '#f44336' }} />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Nouzové kontakty
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {detectedCrisis.severity === 'high' 
              ? 'Detekována kritická situace - okamžitě kontaktujte příslužné služby!'
              : 'Detekována urgentní situace - zde jsou užitečné kontakty:'}
          </Typography>
        </DialogTitle>

        <DialogContent sx={{ p: 0 }}>
          <List>
            {emergencyContacts.map((contact, index) => (
              <React.Fragment key={index}>
                <ListItem
                  sx={{
                    py: 2,
                    cursor: 'pointer',
                    '&:hover': {
                      bgcolor: 'rgba(0,0,0,0.04)'
                    }
                  }}
                  onClick={() => handleEmergencyCall(contact.number)}
                >
                  <ListItemIcon>
                    {contact.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                          {contact.name}
                        </Typography>
                        <Chip
                          label={contact.number}
                          size="small"
                          color="primary"
                          sx={{ fontWeight: 600 }}
                        />
                      </Box>
                    }
                    secondary={
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          {contact.description}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ 
                          display: 'flex', 
                          alignItems: 'center',
                          mt: 0.5
                        }}>
                          <TimeIcon sx={{ fontSize: 14, mr: 0.5 }} />
                          {contact.when}
                        </Typography>
                      </Box>
                    }
                  />
                </ListItem>
                {index < emergencyContacts.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>
        </DialogContent>

        <DialogActions sx={{ p: 3 }}>
          <Button
            onClick={() => setDialogOpen(false)}
            variant="outlined"
            sx={{
              borderRadius: 2,
              color: '#1F1F1F',
              borderColor: '#1F1F1F',
              '&:hover': { borderColor: '#1F1F1F', backgroundColor: 'rgba(31, 31, 31, 0.08)' }
            }}
          >
            Zavřít
          </Button>
          <Button
            onClick={() => handleEmergencyCall('112')}
            variant="outlined"
            startIcon={<PhoneIcon />}
            sx={{
              borderRadius: 2,
              fontWeight: 600,
              bgcolor: '#1F1F1F !important',
              color: '#fff !important',
              '&:hover': { bgcolor: '#e72f0d !important' }
            }}
          >
            Zavolat 112
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CrisisDetector;